/**
 * API Client — fetch wrapper with JWT in memory + auto-refresh.
 * Layer: C (UX/CLI)
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

let accessToken: string | null = null;
let refreshToken: string | null = null;

// ── Token management ──────────────────────────────────────────────────

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
}

export function getAccessToken() { return accessToken; }
export function getRefreshToken() { return refreshToken; }

// ── Core fetch ────────────────────────────────────────────────────────

async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  // Auto-refresh on 401
  if (res.status === 401 && refreshToken && !path.includes('/auth/refresh')) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers.set('Authorization', `Bearer ${accessToken}`);
      const retry = await fetch(`${BASE}${path}`, { ...options, headers });
      if (!retry.ok) throw new ApiError(retry.status, await retry.json());
      if (retry.status === 204) return {} as T;
      return retry.json();
    }
    clearTokens();
    window.location.href = '/login';
    throw new ApiError(401, { error: 'session_expired', message: 'Sessionen har gått ut.' });
  }

  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  if (res.status === 204) return {} as T;
  return res.json();
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export class ApiError extends Error {
  status: number;
  data: Record<string, unknown>;
  constructor(status: number, data: Record<string, unknown>) {
    super((data.message as string) ?? `API error ${status}`);
    this.status = status;
    this.data = data;
  }
}

// ── Typed endpoints ───────────────────────────────────────────────────

export const auth = {
  login: (email: string, password: string) =>
    apiFetch<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (email: string, password: string, tenant_name: string) =>
    apiFetch<LoginResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, tenant_name }),
    }),
  me: () => apiFetch<MeResponse>('/api/auth/me'),
  logout: () =>
    apiFetch('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
};

export const conversations = {
  list: (limit = 50, offset = 0) =>
    apiFetch<{ conversations: Conversation[] }>(`/api/conversations?limit=${limit}&offset=${offset}`),
  get: (id: string) =>
    apiFetch<{ conversation: Conversation; messages: Message[] }>(`/api/conversations/${id}`),
  create: (model?: string, title?: string) =>
    apiFetch<Conversation>('/api/conversations', {
      method: 'POST',
      body: JSON.stringify({ model, title }),
    }),
  delete: (id: string) =>
    apiFetch(`/api/conversations/${id}`, { method: 'DELETE' }),
};

export const settings = {
  get: () => apiFetch<{ tenant: TenantInfo }>('/api/settings'),
  keys: {
    list: () => apiFetch<{ keys: ProviderKeyInfo[] }>('/api/settings/keys'),
    add: (provider: string, api_key: string, base_url?: string) =>
      apiFetch('/api/settings/keys', {
        method: 'POST',
        body: JSON.stringify({ provider, api_key, base_url }),
      }),
    remove: (provider: string) =>
      apiFetch(`/api/settings/keys/${provider}`, { method: 'DELETE' }),
  },
  mcp: {
    list: () => apiFetch<{ servers: McpServerInfo[] }>('/api/settings/mcp'),
    add: (name: string, server_url: string, api_key?: string, target?: string) =>
      apiFetch('/api/settings/mcp', {
        method: 'POST',
        body: JSON.stringify({ name, server_url, api_key, target }),
      }),
    remove: (id: string) =>
      apiFetch(`/api/settings/mcp/${id}`, { method: 'DELETE' }),
  },
  routing: {
    get: () => apiFetch<{ routing: RoutingConfig }>('/api/settings/routing'),
    update: (config: Partial<RoutingConfig>) =>
      apiFetch<{ routing: RoutingConfig }>('/api/settings/routing', {
        method: 'PUT',
        body: JSON.stringify(config),
      }),
  },
};

// ── Types ─────────────────────────────────────────────────────────────

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; role: string };
  tenant: { id: string; name: string; plan: string; subscription_status: string };
}

export interface MeResponse {
  user: { id: string; email: string; role: string; created_at: string };
  tenant: { id: string; name: string; tenant_type: string; subscription_status: string; plan: string } | null;
}

export interface Conversation {
  id: string;
  tenant_id: string;
  user_id: string;
  model: string;
  title: string | null;
  total_tokens: number;
  total_cost_usd: number;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  model: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_usd: number | null;
  created_at: string;
}

export interface ProviderKeyInfo {
  provider: string;
  created_at: string;
  last_used_at: string | null;
  base_url: string | null;
}

export interface McpServerInfo {
  id: string;
  name: string;
  server_url: string;
  target: string | null;
  health_status: string;
  tools_count: number;
  created_at: string;
  updated_at: string;
}

export interface TenantInfo {
  id: string;
  name: string;
  plan: string;
  subscription_status: string;
}

export interface RoutingConfig {
  mode: 'auto' | 'manual';
  default_provider: string;
  default_model: string;
  budget_monthly_usd: number | null;
  rules: Array<{ task: string; provider: string; model: string }>;
}
